import { useEffect, useMemo, useState } from "react";
import { Row, Col, Form, InputGroup } from "react-bootstrap";
import { FaCalendarAlt } from "react-icons/fa";
import CustomSelect from "../../../../../components/CustomSelect/CustomSelect";
import "./ParcelaDados.css";

interface ParcelaDadosProps {
  title?: string;
  readOnly?: boolean;
  onChange?: (data: Record<string, unknown>) => void;
  initialData?: Record<string, unknown>;
  lockCondicao?: boolean;
}

const ParcelaDados = ({
  title = "Dados do Pagamento",
  readOnly = false,
  onChange,
  initialData,
  lockCondicao = false,
}: ParcelaDadosProps) => {
  const condicoes = useMemo(() => {
    const arr = initialData?.condicaoPagamento;
    if (Array.isArray(arr) && arr.length) {
      const labels = arr
        .map((c) => String(c?.titulo || c?.codigo || "").trim())
        .filter(Boolean);
      if (labels.length) return labels;
    }
    return ["7 dias", "10 dias", "15 dias", "30 dias", "45 dias", "60 dias"];
  }, [initialData]);

  const extrairDias = (txt) => {
    const m = String(txt).match(/(\d+)\s*dias?/i);
    if (!m) return 0;
    return parseInt(m[1], 10) || 0;
  };

  const hojeISO = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const somarDiasISO = (dataIso, dias) => {
    const d = new Date(`${dataIso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return dataIso;
    d.setDate(d.getDate() + dias);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const toISODate = (dt) => {
    if (!dt) return "";
    const raw = String(dt);
    if (raw.startsWith("0001-01-01")) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const d = new Date(dt);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    if (yyyy <= 1901) return "";
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const pickCondicaoFromInitialData = (data) => {
    const arr = data?.condicaoPagamento;
    if (Array.isArray(arr) && arr.length) {
      const sel =
        arr.find((x) => x?.selecionado || x?.selected) ||
        arr.find((x) => Number(x?.id) === Number(data?.condicaoPagamentoId)) ||
        arr[0];
      return sel?.titulo || sel?.codigo || "10 dias";
    }
    if (typeof data?.condicaoPagamento === "string" && data.condicaoPagamento) {
      return data.condicaoPagamento;
    }
    return "10 dias";
  };

  const resolveCondicaoId = (titulo: string, data) => {
    const arr = data?.condicaoPagamento;
    if (!Array.isArray(arr)) return Number(data?.condicaoPagamentoId) || 0;
    const found = arr.find(
      (x) => String(x?.titulo || x?.codigo || "").trim() === String(titulo).trim(),
    );
    return Number(found?.id || data?.condicaoPagamentoId) || 0;
  };

  const [descricao, setDescricao] = useState("");
  const [condicao, setCondicao] = useState("10 dias");
  const [dataEmissao, setDataEmissao] = useState("");
  const [dataVencimento, setDataVencimento] = useState("");
  const [dataAprovacaoPAG, setDataAprovacaoPAG] = useState("");
  const [numeroPAG, setNumeroPAG] = useState("");
  const [titulo, setTitulo] = useState("");

  useEffect(() => {
    const base = initialData || {};

    let cond = pickCondicaoFromInitialData(base);
    if (condicoes.length && !condicoes.includes(cond)) {
      cond = condicoes.includes("10 dias") ? "10 dias" : condicoes[0];
    }
    setCondicao(cond);

    const em = toISODate(base?.dataEmissao) || (readOnly ? "" : hojeISO());
    setDataEmissao(em);
    setDataAprovacaoPAG(toISODate(base?.dataAprovacaoPAG));
    setDataVencimento(
      toISODate(base?.dataVencimento) || (em ? somarDiasISO(em, extrairDias(cond)) : ""),
    );
    setDescricao(String(base?.descricao ?? base?.observacao ?? ""));
    setNumeroPAG(String(base?.numeroPAG ?? ""));
    setTitulo(String(base?.numeroTitulo ?? base?.titulo ?? ""));
  }, [initialData, condicoes, readOnly]);

  useEffect(() => {
    if (!dataEmissao) return;
    const dias = extrairDias(condicao);
    setDataVencimento(somarDiasISO(dataEmissao, dias));
  }, [dataEmissao, condicao]);

  const emitChange = (patch: Record<string, unknown>) => {
    const nextCondicao = String(patch.condicao ?? condicao);
    onChange?.({
      descricao,
      condicao: nextCondicao,
      condicaoPagamentoId: resolveCondicaoId(nextCondicao, initialData),
      dataEmissao,
      dataVencimento,
      numeroPAG,
      dataAprovacaoPAG,
      titulo,
      ...patch,
    });
  };

  return (
    <div className="card shadow-sm p-4 mb-4 card-rounded">
      <h5 className="fw-bold text-uppercase mb-4 text-primary">{title}</h5>

      <Row className="g-3">
        <Col md={12}>
          <Form.Label className="form-label-uppersmall">DESCRIÇÃO</Form.Label>
          <Form.Control
            value={descricao}
            placeholder="Programa Meritocracia Sindical 2026 - [Nº] Solicitação"
            disabled={readOnly}
            onChange={(e) => {
              const v = e.target.value;
              if (v.length <= 200) {
                setDescricao(v);
                emitChange({ descricao: v });
              }
            }}
          />
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col md={4}>
          <Form.Label className="form-label-uppersmall">
            DATA DE EMISSÃO
          </Form.Label>
          <InputGroup>
            <InputGroup.Text>
              <FaCalendarAlt />
            </InputGroup.Text>
            <Form.Control
              type="date"
              value={dataEmissao}
              disabled={true}
              placeholder="dd/mm/aaaa"
            />
          </InputGroup>
        </Col>

        <Col md={4}>
          <Form.Label className="form-label-uppersmall">
            CONDIÇÃO DE PAGAMENTO
          </Form.Label>
          <CustomSelect
            id="condicao-pagamento-select"
            ariaLabel="Selecionar condição de pagamento"
            value={condicao}
            options={condicoes.map((opt) => ({ value: opt, label: opt }))}
            onChange={(value) => {
              const v = String(value);
              setCondicao(v);
              emitChange({
                condicao: v,
                dataVencimento: somarDiasISO(dataEmissao, extrairDias(v)),
              });
            }}
            disabled={readOnly || lockCondicao}
          />
        </Col>

        <Col md={4}>
          <Form.Label className="form-label-uppersmall">
            DATA DE VENCIMENTO
          </Form.Label>
          <InputGroup>
            <InputGroup.Text>
              <FaCalendarAlt />
            </InputGroup.Text>
            <Form.Control type="date" value={dataVencimento} disabled={true} />
          </InputGroup>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col md={4}>
          <Form.Label className="form-label-uppersmall">
            NÚMERO DO PAG
          </Form.Label>
          <Form.Control
            value={numeroPAG}
            placeholder="PAG"
            disabled={true}
            readOnly
          />
        </Col>

        <Col md={4}>
          <Form.Label className="form-label-uppersmall">
            DATA DE APROVAÇÃO DO PAG
          </Form.Label>
          <InputGroup>
            <InputGroup.Text>
              <FaCalendarAlt />
            </InputGroup.Text>
            <Form.Control
              type="date"
              value={dataAprovacaoPAG}
              disabled={true}
            />
          </InputGroup>
        </Col>

        <Col md={4}>
          <Form.Label className="form-label-uppersmall">
            NÚMERO DO TÍTULO
          </Form.Label>
          <Form.Control
            value={titulo}
            placeholder="XXXX"
            disabled={true}
            readOnly
          />
        </Col>
      </Row>
    </div>
  );
};

export default ParcelaDados;
